import { useState, useEffect, useCallback, useRef } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { typedApi } from '../api/typed';
import { format } from 'date-fns';
import { toast } from 'sonner';
import useRefreshOnFocus from './useRefreshOnFocus';

/**
 * @param {object} [initial] Optional seed data from a route loader. When
 * provided, the hook skips its `loading=true` flash on first mount so the
 * view paints hydrated.
 */
export default function useTodayData(initial) {
  const seed = initial || {};
  const [tasks, setTasks] = useState(seed.tasks || []);
  const [blocks, setBlocks] = useState(seed.blocks || []);
  const [decisions, setDecisions] = useState(seed.decisions?.decisions || []);
  const [businessPressure, setBusinessPressure] = useState(seed.decisions?.businessPressure || []);
  const [decisionSummary, setDecisionSummary] = useState(seed.decisions?.summary || null);
  const [aiPrompts, setAiPrompts] = useState(seed.decisions?.aiPrompts || []);
  const [loading, setLoading] = useState(!initial);
  const [loadError, setLoadError] = useState(null);

  const { activeBusiness } = useBusiness();
  const today = format(new Date(), 'yyyy-MM-dd');

  // Track the in-flight controller so a new `fetchAll` cancels the previous one.
  const inflightRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; inflightRef.current?.abort(); };
  }, []);

  const fetchDecisions = useCallback(async (signal) => {
    try {
      const params = {};
      if (activeBusiness) params.businessId = activeBusiness.id;
      const data = await typedApi.get('/api/decisions/queue', { params, signal });
      if (!mountedRef.current) return;
      setDecisions(data.decisions || []);
      setBusinessPressure(data.businessPressure || []);
      setDecisionSummary(data.summary || null);
      setAiPrompts(data.aiPrompts || []);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not load decisions');
    }
  }, [activeBusiness]);

  const fetchBlocks = useCallback(async (signal) => {
    try {
      const data = await typedApi.get('/api/timeblocks', { params: { date: today }, signal });
      if (!mountedRef.current) return;
      setBlocks(data);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not load time blocks');
    }
  }, [today]);

  const fetchTasks = useCallback(async (signal) => {
    try {
      const params = {};
      if (activeBusiness) params.businessId = activeBusiness.id;
      const [scheduledTasks, todayTasks] = await Promise.all([
        typedApi.get('/api/tasks', { params: { ...params, scheduledDate: today }, signal }),
        typedApi.get('/api/tasks', { params: { ...params, status: 'TODAY' }, signal }),
      ]);
      if (!mountedRef.current) return;
      const allTasks = [...scheduledTasks, ...todayTasks];
      setTasks(Array.from(new Map(allTasks.map(t => [t.id, t])).values()));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      toast.error('Could not load tasks');
    }
  }, [today, activeBusiness]);

  const hasLoadedRef = useRef(Boolean(initial));

  const fetchAll = useCallback(async () => {
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    if (mountedRef.current && !hasLoadedRef.current) setLoading(true);
    setLoadError(null);
    try {
      // One round-trip for the critical Today path.
      const params = {};
      if (activeBusiness) params.businessId = activeBusiness.id;
      const bundle = await typedApi.get('/api/today/bundle', { params, signal: controller.signal });
      if (!mountedRef.current || controller.signal.aborted) return;
      setTasks(bundle.tasks || []);
      setBlocks(bundle.blocks || []);
      const d = bundle.decisions || {};
      setDecisions(d.decisions || []);
      setBusinessPressure(d.businessPressure || []);
      setDecisionSummary(d.summary || null);
      setAiPrompts(d.aiPrompts || []);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      // Fall back to individual endpoints if bundle fails (resilience).
      try {
        await Promise.all([
          fetchDecisions(controller.signal),
          fetchBlocks(controller.signal),
          fetchTasks(controller.signal),
        ]);
      } catch (fallbackErr) {
        if (mountedRef.current) setLoadError(fallbackErr || err);
        toast.error('Could not load Today view');
      }
    } finally {
      if (mountedRef.current && !controller.signal.aborted) setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [activeBusiness, fetchDecisions, fetchBlocks, fetchTasks]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Silently refresh when user returns to tab after 30s+
  useRefreshOnFocus(fetchAll);

  // Listen for cross-app mutation broadcasts so Today reflects changes
  // triggered from other views (Inbox triage, Payments PAID, etc.).
  // Debounced 120ms to coalesce bursts (e.g. bulk operations fire many
  // mutations in quick succession).
  useEffect(() => {
    let timeoutId = null;
    const handler = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (mountedRef.current) fetchAll();
      }, 120);
    };
    window.addEventListener('polkdesq:data-mutated', handler);
    return () => {
      window.removeEventListener('polkdesq:data-mutated', handler);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchAll]);

  // Computed
  const activeTasks = tasks.filter(t => t.status !== 'COMPLETED');
  const completedTasks = tasks.filter(t => t.status === 'COMPLETED');
  const urgentCount = activeTasks.filter(t => t.priority === 'URGENT' || t.priority === 'HIGH').length;
  const overduePayments = decisions.filter(d => d.type === 'payment' && d.paymentStatus === 'OVERDUE').length;
  const todayCalls = decisions.filter(d => d.type === 'call' && d.category === 'DO_NOW').length;

  return {
    tasks, setTasks,
    blocks, setBlocks,
    decisions, businessPressure, decisionSummary, aiPrompts,
    loading, today, loadError,
    fetchAll, fetchDecisions, fetchTasks, fetchBlocks,
    activeTasks, completedTasks, urgentCount, overduePayments, todayCalls,
  };
}
